import { useEffect, useRef, type ReactNode } from 'react';

// U-ADM-05 · pre-launch audit hardening.
//
// Twelve pages in this codebase hand-roll <div className="modal-backdrop">
// + <div className="modal">. That copy-paste has three real problems:
//
//   1. Clicking the backdrop discards a partially-filled form with no
//      warning ("did I lose my HR note? — yes, you did").
//   2. There's no focus trap; a keyboard-only user can Tab into the
//      sidebar behind the modal.
//   3. Escape doesn't close and focus isn't returned to the trigger
//      on close.
//
// This component fixes all three:
//   * role="dialog" + aria-modal + aria-labelledby.
//   * Focus is trapped inside the dialog.
//   * Escape closes.
//   * Return focus to the element that had focus before open.
//   * If `dirty` is true, backdrop click / Escape prompt first.
//
// Same visual language (.modal-backdrop + .modal in global.css), so
// existing pages migrate by replacing their outer wrapper.
//
// Follow-up: sweep the remaining CRUD pages onto this primitive and
// delete the raw <div className="modal-backdrop"> soup.

interface Props {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  // When true, a backdrop click / Escape confirms before closing.
  dirty?: boolean;
  dirtyConfirmText?: string;
  // Optional slot for footer actions (buttons). Renders under content
  // with the same spacing existing modals use.
  actions?: ReactNode;
  // Optional extra class on the .modal panel.
  panelClassName?: string;
}

export default function Modal({
  open,
  onClose,
  title,
  children,
  dirty = false,
  dirtyConfirmText = 'فيه تعديلات مش مضمنة — تخرج فعلاً؟',
  actions,
  panelClassName,
}: Props) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Remember the trigger so we can return focus on close.
    returnFocusRef.current = document.activeElement as HTMLElement | null;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        tryClose();
      } else if (e.key === 'Tab') {
        trapTab(e);
      }
    };

    document.addEventListener('keydown', handleKey);
    // Move focus into the panel.
    setTimeout(() => {
      const el = panelRef.current;
      if (!el) return;
      const focusable = el.querySelector<HTMLElement>(FOCUSABLE);
      (focusable ?? el).focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKey);
      // Return focus to whatever opened the dialog.
      returnFocusRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const tryClose = () => {
    if (dirty && !window.confirm(dirtyConfirmText)) return;
    onClose();
  };

  const trapTab = (e: KeyboardEvent) => {
    const el = panelRef.current;
    if (!el) return;
    const list = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (n) => !n.hasAttribute('disabled') && n.tabIndex !== -1,
    );
    if (list.length === 0) {
      e.preventDefault();
      return;
    }
    const first = list[0];
    const last = list[list.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="modal-backdrop" onClick={tryClose}>
      <div
        ref={panelRef}
        className={panelClassName ? `modal ${panelClassName}` : 'modal'}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={typeof title === 'string' ? 'mersal-modal-title' : undefined}
        tabIndex={-1}
      >
        {(title || true) && (
          <div className="modal-header">
            {typeof title === 'string' ? (
              <h2 id="mersal-modal-title">{title}</h2>
            ) : (
              title
            )}
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={tryClose}
              aria-label="إغلاق"
            >
              إغلاق
            </button>
          </div>
        )}
        {children}
        {actions && <div className="modal-actions">{actions}</div>}
      </div>
    </div>
  );
}

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
